/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : confirmStcSlice.ts
 *
 * Project Title : RDR7000
 *
 * Description   : Redux Slice for Confirm STC state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : Aug 31, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : Aug 31, 2020
 * Description   : Add code to maintain state for Confirm STC data
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ISoftwareFeatures } from 'components/SoftwareFeatures/SoftwareFeaturesItem/ISoftwareFeaturesItemProps';
import { IConfirmStcState, IConfirmSTCField, IConfirmStcFormFields, IConfirmSTCData } from 'models/confirmSTC';
import { AppThunk, RootState } from 'stores/GlobalStore';
import { apiCallBegan } from 'stores/middlewares';

export const initialState: IConfirmStcState = {
  stcMasterData: null,
  stcMasterLoader: false,
  selectedConfirmStc: null,
  confirmStcData: null,
  softDetails : null
};

const confirmStcSlice = createSlice({
  name: 'confirmStc',
  initialState,
  reducers: {
    setStcMasterData(confirmStcState: IConfirmStcState, action: PayloadAction<IConfirmSTCField>) {
      confirmStcState.stcMasterData = action.payload;
      confirmStcState.stcMasterLoader = false;
    },
    beginStcMasterLoader(confirmStcState: IConfirmStcState) {
      confirmStcState.stcMasterLoader = true;
    },
    resetStcMasterDataOnError(confirmStcState: IConfirmStcState) {
      confirmStcState.stcMasterData = null;
      confirmStcState.stcMasterLoader = false;
    },
    setSelectedConfirmStc(confirmStcState: IConfirmStcState, action: PayloadAction<IConfirmStcFormFields>) {
      confirmStcState.selectedConfirmStc = action.payload;
    },
    setConfirmStcData(confirmStcState: IConfirmStcState, action: PayloadAction<IConfirmSTCData>) {
      confirmStcState.confirmStcData = action.payload;
      confirmStcState.stcMasterLoader = false;
    },
    resetConfirmStcDataOnError(confirmStcState: IConfirmStcState) {
      confirmStcState.confirmStcData = null;
      confirmStcState.selectedConfirmStc = null;
      confirmStcState.stcMasterLoader = false;
    },
    setSoftFeatures (confirmStcState: IConfirmStcState, action: PayloadAction<ISoftwareFeatures>) {
      confirmStcState.softDetails = action.payload;
      confirmStcState.stcMasterLoader = false;
    },
    resetSoftFeatures(confirmStcState: IConfirmStcState) {
      confirmStcState.softDetails = null;
      confirmStcState.stcMasterLoader = false;
    },
  },
});

const { setStcMasterData, beginStcMasterLoader, resetStcMasterDataOnError, setSoftFeatures } = confirmStcSlice.actions;

export const { setSelectedConfirmStc, setConfirmStcData, resetConfirmStcDataOnError } = confirmStcSlice.actions;

export const confirmStcReducer = confirmStcSlice.reducer;

export const fetchStcMasterData = (): AppThunk => async (dispatch, getState) => {
  if (getState().confirmStc.stcMasterData !== null) {
    return;
  }

  const url = '/stc-masterdata';

  dispatch(
    apiCallBegan({
      url,
      onStart: beginStcMasterLoader.type,
      onSuccess: setStcMasterData.type,
      onError: resetStcMasterDataOnError.type,
      showGlobalError: true,
    })
  );
};

export const fetchConfirmStcData = (
  configId: string,
  soldTo: string,
  salesOrderNumber: string
): AppThunk => async dispatch => {
  const url = `/stc-masterdata/${salesOrderNumber}`;
  const params = {
    'config-id': configId,
    'sold-to': soldTo,
  };

  dispatch(
    apiCallBegan({
      url,
      params,
      onStart: beginStcMasterLoader.type,
      onSuccess: setConfirmStcData.type,
      onError: resetConfirmStcDataOnError.type,
      showGlobalError: true,
    })
  );
};

export const fetchSoftwareFeatureDetails = (
  make: string,
  model: string,
  stc : string,
  installMode : string,
  soldTo: string,
  salesOrderNumber: string
): AppThunk => async dispatch => {
  const url = `/configure-screen-data/selected-configuration/${salesOrderNumber}/${soldTo}`;
  const params = {
    'make': make,
    'model': model,
    'stc' : stc,
    'install-mode' : installMode
  };

  dispatch(
    apiCallBegan({
      url,
      params,
      onStart: beginStcMasterLoader.type,
      onSuccess: setSoftFeatures.type,
      onError: resetConfirmStcDataOnError.type,
      showGlobalError: true,
    })
  );
};

export const selectStcMasterData = (state: RootState) => state.confirmStc.stcMasterData;
export const selectStcMasterLoader = (state: RootState) => state.confirmStc.stcMasterLoader;
export const selectConfirmStcData = (state: RootState) => state.confirmStc.confirmStcData;
export const selectSelectedConfirmStc = (state: RootState) => state.confirmStc.selectedConfirmStc;
export const selectSoftDetailsData = (state: RootState) => state.confirmStc.softDetails;